import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { Eye, Trash2, FileText, Plus, Loader2, AlertCircle } from 'lucide-react';

import Extraction from './Extraction';
import Analysis from './Analysis';

import {
  ProcessingWrapper,
  ProcessingContainer,
  HeaderSection,
  SectionTitle,
  SectionDescription,
  ActionButtons,
  DashboardButton,
  // SaveResultsButton,
  ContentLayout,
  DocumentsPanel,
  PanelTitle,
  DocumentsList,
  DocumentItem,
  DocIconWrapper,
  DocInfo,
  DocName,
  DocSize,
  DocActions,
  DocActionButton,
  UploadMoreButton,
  ResultsPanel,
  TabsBar,
  Tab,
  TabContent,
  GlobalError,
  LoadingState,
} from './processing.styles';

import {
  PROCESSING_KEYS,
  useGetList,
  // useUpdateList,
} from './hook/Useprocessing';
import {
  useSaveDocument,
  useDeleteDocument,
  useViewDocument,
} from '../UploadDocuments/hooks/Useuploaddocuments';

import { formatFileSize, getUploadedAt } from '../UploadDocuments/utils';

const TABS = ['Extraction', 'Analysis'];

const createFolderName = () => uuidv4().replace(/-/g, '');

const Processing = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('Extraction');
  const [documents, setDocuments] = useState([]);
  const [folderName, setFolderName] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [docLoadingState, setDocLoadingState] = useState({});

  const queryClient = useQueryClient();

  // ─── Fetch list ────────────────────────────────────────────────────────────
  const {
    data: listData,
    isLoading: isLoadingList,
    isError: isListError,
    error: listError,
    refetch: refetchList,
  } = useGetList(id);

  // Hydrate documents + folderName once list loads
  // API response shape: { list: { _id, folderArray, pdfArray, extractionStatus, ... }, message }
  // The list now carries a folderArray (one entry per root folder used during
  // upload) rather than a single top-level folderName. "Upload More" needs
  // *some* folderName to attach new files to, so we take the first entry —
  // falling back to a freshly generated one only if the list somehow has
  // none yet (shouldn't normally happen once at least one doc exists).
  useEffect(() => {
    if (listData) {
      const list = listData.list ?? listData; // handle both wrapped and flat shapes

      const folders =
        list.folderArray ?? (list.folderName ? [{ folderName: list.folderName }] : []);
      setFolderName(folders[0]?.folderName || createFolderName());

      const mapped = (list.pdfArray ?? []).map((pdf, index) => ({
        id: pdf.documentId,
        // API only returns documentId — use a short label until a real name is available
        name: pdf.fileName ?? pdf.documentName ?? `Document ${index + 1}`,
        size: pdf.fileSize ? formatFileSize(pdf.fileSize) : '—',
        uploadedAt: pdf.uploadedAt ?? '',
      }));
      setDocuments(mapped);
    }
  }, [listData]);

  // ─── Upload more ───────────────────────────────────────────────────────────
  const { mutate: uploadDocument, isPending: isUploading } = useSaveDocument(folderName, {
    onSuccess: (data, { file }) => {
      setDocuments((prev) => [
        ...prev,
        {
          id: data.documentId,
          name: file.name,
          size: formatFileSize(file.size),
          uploadedAt: `Uploaded at ${getUploadedAt()}`,
        },
      ]);
      setGlobalError('');
      // Refetch this exact list — it's what drives listData/isLoadingList in
      // this component, so this is the most direct way to get the UI to
      // pick up the newly-uploaded document.
      setActiveTab('Extraction');
      refetchList();
    },
    onError: (err) => setGlobalError(err?.message || 'Failed to upload document.'),
  });

  const { mutate: viewDoc } = useViewDocument(id, {
    onSuccess: (data, variables) => {
      const { documentId } = variables;
      setDocLoadingState((prev) => {
        const n = { ...prev };
        delete n[documentId];
        return n;
      });
      // Open the document — check both common field names the API might return
      const url = data?.documentUrl ?? data?.url;
      if (url) window.open(url, '_blank');
    },
    onError: (error, variables) => {
      const { documentId } = variables;
      setDocLoadingState((prev) => {
        const n = { ...prev };
        delete n[documentId];
        return n;
      });
      setGlobalError(error?.message || 'Failed to open document.');
    },
  });

  const { mutate: deleteDoc } = useDeleteDocument(id, {
    onSuccess: (data, variables) => {
      const { documentId } = variables;
      setDocuments((prev) => prev.filter((d) => d.id !== documentId));
      setDocLoadingState((prev) => {
        const n = { ...prev };
        delete n[documentId];
        return n;
      });
      setGlobalError('');
      // Same as upload — refetch this exact list directly.
      setActiveTab('Extraction');
      refetchList();
    },
    onError: (error, variables) => {
      const { documentId } = variables;
      setDocLoadingState((prev) => {
        const n = { ...prev };
        delete n[documentId];
        return n;
      });
      setGlobalError(error?.message || 'Failed to delete document.');
    },
  });

  // ─── Update list (Save Results) ────────────────────────────────────────────
  // Not needed for now — commented out, logic kept for when it's re-enabled.
  // const { mutate: updateList, isPending: isSaving } = useUpdateList({
  //   onSuccess: () => setGlobalError(''),
  //   onError: (err) => setGlobalError(err?.message || 'Failed to save results.'),
  // });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleFileSelect = useCallback(
    (file) => {
      setGlobalError('');
      uploadDocument({ file, isExisting: true, listId: id });
    },
    [uploadDocument, id]
  );

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
      e.target.value = '';
    }
  };

  const handleUploadMore = () => {
    document.getElementById('processing-file-input')?.click();
  };

  const handleView = useCallback(
    (doc) => {
      setDocLoadingState((prev) => ({ ...prev, [doc.id]: 'viewing' }));
      setGlobalError('');
      viewDoc({ documentId: doc.id });
    },
    [viewDoc]
  );

  const handleDelete = useCallback(
    (doc) => {
      setDocLoadingState((prev) => ({ ...prev, [doc.id]: 'deleting' }));
      setGlobalError('');
      deleteDoc({ documentId: doc.id });
    },
    [deleteDoc]
  );

  // const handleSave = () => {
  //   setGlobalError('');
  //   updateList(
  //     {
  //       listId: id,
  //       pdfArray: documents.map((d) => ({ documentId: d.id })),
  //     },
  //     {
  //       onSuccess: () => {
  //         setActiveTab('Extraction');
  //         queryClient.invalidateQueries({ queryKey: PROCESSING_KEYS.extractions(id) });
  //         queryClient.invalidateQueries({ queryKey: PROCESSING_KEYS.analysis(id) });
  //         queryClient.invalidateQueries({ queryKey: PROCESSING_KEYS.list(id) });
  //       },
  //       onError: (err) => setGlobalError(err?.message || 'Failed to save results.'),
  //     }
  //   );
  // };

  const handleDashboard = () => navigate('/');

  // ─── Loading / Error guards ────────────────────────────────────────────────

  if (isLoadingList) {
    return (
      <ProcessingWrapper>
        <ProcessingContainer>
          <LoadingState>
            <Loader2 size={24} className="spin" />
            <span>Loading document list…</span>
          </LoadingState>
        </ProcessingContainer>
      </ProcessingWrapper>
    );
  }

  if (isListError) {
    return (
      <ProcessingWrapper>
        <ProcessingContainer>
          <GlobalError role="alert">
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            {listError?.message || 'Failed to load document list.'}
          </GlobalError>
        </ProcessingContainer>
      </ProcessingWrapper>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <ProcessingWrapper>
      <ProcessingContainer>
        {/* Header */}
        <HeaderSection>
          <div>
            <SectionTitle>Processing Result</SectionTitle>
            <SectionDescription>
              View extraction and analysis results from your documents
            </SectionDescription>
          </div>
          <ActionButtons>
            <DashboardButton onClick={handleDashboard} type="button">
              Dashboard
            </DashboardButton>
            {/* Save Results not needed for now */}
            {/* <SaveResultsButton onClick={handleSave} disabled={isSaving} type="button">
              {isSaving ? (
                <Loader2
                  size={14}
                  className="spin"
                  style={{ animation: 'spin 1s linear infinite' }}
                />
              ) : null}
              {isSaving ? 'Saving…' : 'Save Results'}
            </SaveResultsButton> */}
          </ActionButtons>
        </HeaderSection>

        {/* Global error */}
        {globalError && (
          <GlobalError role="alert">
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            {globalError}
          </GlobalError>
        )}

        {/* Main layout */}
        <ContentLayout>
          {/* Left — Documents panel */}
          <DocumentsPanel>
            <PanelTitle>
              Uploaded Documents
              {documents.length > 0 && (
                <span
                  style={{ marginLeft: 6, fontWeight: 400, color: 'var(--color-text-tertiary)' }}
                >
                  ({documents.length})
                </span>
              )}
            </PanelTitle>

            <DocumentsList className="hide-scroll-bar">
              {documents.map((doc) => {
                const isViewing = docLoadingState[doc.id] === 'viewing';
                const isDeleting = docLoadingState[doc.id] === 'deleting';

                return (
                  <DocumentItem key={doc.id}>
                    <DocIconWrapper>
                      <FileText size={16} />
                    </DocIconWrapper>

                    <DocInfo>
                      <DocName title={doc.name}>{doc.name}</DocName>
                      {/* <DocSize>{doc.size}</DocSize> */}
                    </DocInfo>

                    <DocActions>
                      <DocActionButton
                        title="View document"
                        onClick={() => handleView(doc)}
                        disabled={isViewing || isDeleting}
                        aria-label={`View ${doc.name}`}
                      >
                        {isViewing ? <Loader2 size={14} className="spin" /> : <Eye size={14} />}
                      </DocActionButton>

                      <DocActionButton
                        className="delete"
                        title="Delete document"
                        onClick={() => handleDelete(doc)}
                        disabled={isViewing || isDeleting}
                        aria-label={`Delete ${doc.name}`}
                      >
                        {isDeleting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                      </DocActionButton>
                    </DocActions>
                  </DocumentItem>
                );
              })}
            </DocumentsList>

            <UploadMoreButton
              onClick={handleUploadMore}
              disabled={isUploading || !folderName}
              type="button"
            >
              {isUploading ? (
                <>
                  <Loader2 size={14} className="spin" /> Uploading…
                </>
              ) : (
                <>
                  <Plus size={14} /> Upload More Documents
                </>
              )}
            </UploadMoreButton>

            <input
              id="processing-file-input"
              type="file"
              accept=".pdf"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              aria-hidden="true"
            />
          </DocumentsPanel>

          {/* Right — Tabbed results */}
          <ResultsPanel>
            <TabsBar>
              {TABS.map((tab) => (
                <Tab
                  key={tab}
                  $active={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                  type="button"
                >
                  {tab}
                </Tab>
              ))}
            </TabsBar>

            <TabContent>
              {activeTab === 'Extraction' && (
                <Extraction
                  listId={id}
                  initialExtractionStatus={(listData?.list ?? listData)?.extractionStatus}
                  onRefetchList={refetchList}
                />
              )}
              {activeTab === 'Analysis' && (
                <Analysis
                  listId={id}
                  initialAnalysisStatus={(listData?.list ?? listData)?.analysisStatus}
                />
              )}
            </TabContent>
          </ResultsPanel>
        </ContentLayout>
      </ProcessingContainer>
    </ProcessingWrapper>
  );
};

export default Processing;
